import { useCallback, useEffect, useRef, useState } from 'react';
import NoSleep from '@zakj/no-sleep';

/**
 * Provides a screen wake-lock with native API first and NoSleep fallback.
 * Exposes request/release so callers control when the lock is active.
 */
const useWakeLock = ({ onError } = {}) => {
    const wakeLockRef = useRef(null);
    const noSleepRef = useRef(null);
    const [isActive, setIsActive] = useState(false);
    const [unavailableReason, setUnavailableReason] = useState(null);

    const handleError = useCallback((err) => {
        const reason = err?.message || 'Wake lock unavailable';
        setUnavailableReason(reason);
        onError?.(err);
    }, [onError]);

    const release = useCallback(async () => {
        if (wakeLockRef.current) {
            try {
                await wakeLockRef.current.release?.();
            } catch {
                // Best-effort release; ignore errors.
            }
            wakeLockRef.current = null;
        }

        if (noSleepRef.current) {
            try {
                noSleepRef.current.disable();
            } catch {
                // Best-effort disable; ignore errors.
            }
        }

        setIsActive(false);
    }, []);

    const requestNative = useCallback(async () => {
        if (!('wakeLock' in navigator)) {
            return false;
        }
        try {
            const lock = await navigator.wakeLock.request('screen');
            wakeLockRef.current = lock;
            setUnavailableReason(null);
            setIsActive(true);
            lock.addEventListener('release', () => {
                setIsActive(false);
            });
            return true;
        } catch (err) {
            handleError(err);
            return false;
        }
    }, [handleError]);

    const requestNoSleep = useCallback(async () => {
        try {
            if (!noSleepRef.current) {
                noSleepRef.current = new NoSleep();
            }
            await noSleepRef.current.enable();
            setUnavailableReason(null);
            setIsActive(true);
            return true;
        } catch (err) {
            handleError(err);
            return false;
        }
    }, [handleError]);

    const request = useCallback(async () => {
        if (isActive) {
            return true;
        }
        if (await requestNative()) {
            return true;
        }
        return requestNoSleep();
    }, [isActive, requestNative, requestNoSleep]);

    useEffect(() => {
        return () => {
            release();
        };
    }, [release]);

    return {
        request,
        release,
        isActive,
        unavailableReason,
    };
};

export default useWakeLock;
